import React, { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, requireAuth, isTokenValid } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Handle browser back/forward cache: re-check auth on visibility/pageshow
  useEffect(() => {
    const enforceAuth = () => {
      if (loading) return;
      const tokenStillValid = isTokenValid();
      if (!isAuthenticated || !tokenStillValid) {
        navigate('/login', { state: { from: location }, replace: true });
      }
    };

    const handlePageShow = (event) => {
      if (event.persisted || document.visibilityState === 'visible') {
        enforceAuth();
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        enforceAuth();
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibility);

    // Run once on mount as well
    enforceAuth();

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [isAuthenticated, isTokenValid, navigate, location, loading]);

  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#EFF6FF] via-white to-[#F9FAFB]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Check if user is authenticated and token is valid
  if (!isAuthenticated || !requireAuth()) {
    // Redirect to login page with return URL
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // User is authenticated, render the protected content
  return children;
};

export default ProtectedRoute;
